import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Clock, Crown, ArrowRight, Sparkles, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';

interface FreeTrialPopupProps {
  delay?: number;
  isFreemium: boolean;
}

const FreeTrialPopup = ({ delay = 30, isFreemium }: FreeTrialPopupProps) => {
  const [showPopup, setShowPopup] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { createCheckoutSession } = useSubscription();

  useEffect(() => {
    if (!isFreemium || dismissed) return;
    const wasDismissed = sessionStorage.getItem('trialPopupDismissed');
    if (wasDismissed) { setDismissed(true); return; }
    const timer = setTimeout(() => setShowPopup(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [isFreemium, delay, dismissed]);

  useEffect(() => {
    if (!isFreemium || !dismissed) return;
    const reminderTimer = setTimeout(() => {
      sessionStorage.removeItem('trialPopupDismissed');
      setDismissed(false);
    }, 180000);
    return () => clearTimeout(reminderTimer);
  }, [isFreemium, dismissed]);

  const handleDismiss = () => {
    setShowPopup(false);
    setDismissed(true);
    sessionStorage.setItem('trialPopupDismissed', 'true');
  };

  const handleStartTrial = async () => {
    setIsProcessing(true);
    try {
      await createCheckoutSession('monthly');
    } catch (error: any) {
      toast.error(error.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showPopup && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm"
              onClick={handleDismiss}
            />

            {/* Mobile-first bottom sheet style popup */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 80 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 sm:bottom-auto sm:inset-0 z-[61] flex items-end sm:items-center justify-center sm:p-4 pointer-events-none"
            >
              <div className="relative w-full sm:max-w-sm pointer-events-auto">
                {/* Drag handle (mobile) */}
                <div className="flex justify-center pt-3 pb-1 sm:hidden">
                  <div className="w-10 h-1 rounded-full bg-slate-300" />
                </div>

                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="absolute top-3 right-3 sm:-top-2 sm:-right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-95 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Card */}
                <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
                  {/* Compact gradient header */}
                  <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 px-5 py-5 text-center relative overflow-hidden">
                    {/* Subtle sparkle dots */}
                    <div className="absolute inset-0 opacity-15">
                      <div className="absolute top-3 left-6 w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      <div className="absolute top-6 right-10 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                      <div className="absolute bottom-4 left-14 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                    </div>

                    <motion.div
                      initial={{ rotate: -8 }}
                      animate={{ rotate: 8 }}
                      transition={{ repeat: Infinity, repeatType: 'reverse', duration: 2.5, ease: 'easeInOut' }}
                      className="inline-block mb-2"
                    >
                      <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <Crown className="w-6 h-6 text-yellow-300" />
                      </div>
                    </motion.div>

                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-0.5">
                      Unlock Full Access
                    </h2>
                    <p className="text-blue-100 text-xs sm:text-sm">
                      500+ lessons, AI tools & community
                    </p>
                  </div>

                  {/* Body */}
                  <div className="px-5 py-4 sm:p-5">
                    {/* Benefits — compact for mobile */}
                    <div className="space-y-2.5 mb-4">
                      {[
                        { icon: Zap, text: 'Full Faceless Content System unlocked', color: 'text-blue-600 bg-blue-50' },
                        { icon: Sparkles, text: '500+ short lessons & exercises', color: 'text-purple-600 bg-purple-50' },
                        { icon: Clock, text: 'Cancel anytime — no commitment', color: 'text-green-600 bg-green-50' },
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.08 * i + 0.2 }}
                          className="flex items-center gap-3"
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.color.split(' ')[1]}`}>
                            <item.icon className={`w-4 h-4 ${item.color.split(' ')[0]}`} />
                          </div>
                          <span className="text-[13px] sm:text-sm text-slate-700 font-medium">{item.text}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Urgency — slimmer on mobile */}
                    <div className="mb-4 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200/80 text-center">
                      <p className="text-[11px] sm:text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        ⏰ Limited offer — Upgrade now
                      </p>
                    </div>

                    {/* CTA — h-14 touch target */}
                    <Button
                      onClick={handleStartTrial}
                      disabled={isProcessing}
                      className="w-full h-14 text-[15px] font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-600/25 active:scale-[0.98] transition-all"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Get Full Access
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>

                    {/* Trust line */}
                    <div className="flex items-center justify-center gap-1.5 mt-3 mb-1">
                      <Shield className="w-3 h-3 text-green-500" />
                      <p className="text-[10px] sm:text-[11px] text-slate-400">
                        Cancel anytime • Secure via Stripe
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </>
  );
};

export default FreeTrialPopup;
